import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import {
  TeamReportList,
  GameReportListItem,
  TeamReportListItem,
} from "../util/Requests";
import { useReportStore } from "../data/ReportStore";
import { Team } from "../models/Team";
import {
  useLeagueApi,
  useLeagueEnvironment,
  useLeaguePaths,
} from "../data/LeagueEnvironmentProvider";
import { ExplorerEnvironment } from "../data/Cache";
import GamePicker, { PickerGame, PickerPlayer } from "../components/Elements/GamePicker/GamePicker";
import { customReportId, parseReportId } from "./useTeamReport";
import "./teamReportDesign.scss";

const seasonLabel = (season: number) =>
  `${season}-${((season + 1) % 100).toString().padStart(2, "0")}`;

const seasonReportLabel = (report: TeamReportListItem, playoff: boolean, preseason = false) => {
  if (report.preview) {
    return `${seasonLabel(report.season)} · Roster preview (${seasonLabel(report.season - 1)} stats)`;
  }
  if (preseason) return `${seasonLabel(report.season)} · Preseason`;
  return `${seasonLabel(report.season)} · ${playoff ? "Playoffs" : "Regular Season"}`;
};

export type SeasonChoice = { report: TeamReportListItem; playoff: boolean; preseason: boolean };

// Window reports (Last N games, phase 2): a regular season's, as the backend builds them
const WINDOW_TYPES = ["L5", "L10", "L20"] as const;
type WindowType = (typeof WINDOW_TYPES)[number];
// Custom (2026-10-27): any of the season's games, built on request for any team (custom_windows.py)
export type Scope = "season" | "game" | WindowType | "custom";
const WINDOW_LABELS: Record<WindowType, string> = { L5: "Last 5", L10: "Last 10", L20: "Last 20" };
/** A window report id's type and season: "POR-L10-2025" -> { type: "L10", season: 2025 } */
const windowOf = (id?: string | null) => {
  const type = WINDOW_TYPES.find((t) => id?.includes(`-${t}-`));
  return type ? { type, season: Number(id!.split("-").pop()) } : null;
};
/** A custom window report id's season: "POR-custom-<key>-2025" -> { season: 2025 } */
const customOf = (id?: string | null) => {
  const parsed = id ? parseReportId(id) : null;
  return parsed?.customKey && parsed.season ? { season: parsed.season } : null;
};

// A game belongs to the season report of its season and type: playoffs, preseason, else the regular season.
const gameInSeason = (g: GameReportListItem, { report, playoff, preseason }: SeasonChoice) =>
  g.gameInfo.season === report.season &&
  (playoff
    ? g.gameInfo.seasonType === "Playoffs"
    : preseason
      ? g.gameInfo.seasonType === "Preseason"
      : g.gameInfo.seasonType !== "Playoffs" && g.gameInfo.seasonType !== "Preseason");

/**
 * The report to open when the picker switches to another team: the same season and scope where that team has the
 * report, else that season's full regular season. A game becomes the other side of it when the new team played in it;
 * custom picks are the viewed team's own games, so they never carry over. `targetList` is the new team's built reports
 * (null when it could not be fetched: only the always-built regular season is then trusted).
 */
export function teamSwitchReportId(
  target: string,
  staged: { seasonSel?: SeasonChoice; scope: Scope; game?: GameReportListItem },
  targetList: TeamReportList | null,
): string | null {
  const { seasonSel, scope, game } = staged;
  if (!seasonSel) return null;
  const season = seasonSel.report.season;
  const regular = `${target}-season-${season}`;
  const built = (rows?: TeamReportListItem[]) => !!rows?.some((r) => r.season === season);
  const seasonId = seasonSel.playoff
    ? built(targetList?.playoff_season) ? `${target}-playoff_season-${season}` : regular
    : seasonSel.preseason
      ? built(targetList?.preseason_season) ? `${target}-preseason_season-${season}` : regular
      : regular;
  if (scope === "game") {
    return game && game.gameInfo.opponent.abbrev.toUpperCase() === target
      ? `${target}-game-${game.gameInfo.gameId}`
      : seasonId;
  }
  if (scope === "season") return seasonId;
  if (scope === "custom") return regular;
  return built(targetList?.[scope]) ? `${target}-${scope}-${season}` : regular;
}

const gameReportLabel = (report: GameReportListItem) => {
  const { opponent, date, isHome } = report.gameInfo;
  return `${dayjs(date).format("MMM D").toUpperCase()} · ${isHome ? "VS" : "@"} ${opponent.abbrev}`;
};

/**
 * The 1B web masthead: dark picker strip (team / season / scope / game) with the "Viewing" context line under it.
 * Web-only — print hides the whole strip. A pick opens its report at once (Trevor, 2026-11-04): only a pick that is not
 * a report yet waits — Single game until its game is chosen, Custom games until View Report requests the build. Picking
 * a different team opens that team's page on the same season and scope (`teamSwitchReportId`). The picker starts on the report being viewed and follows it when another one opens (a link, a game
 * card), so it doubles as the report's heading (Trevor, 2026-10-19): a game report shows Single game, that game and
 * its season and type.
 */
export default function ReportPickerMasthead({
  reportList,
  selectedReportId,
  onReportChange,
  availableGameIds = [],
  loading = false,
  windowGameIds,
}: {
  reportList: TeamReportList;
  selectedReportId?: string | null;
  onReportChange?: (reportId: string) => void;
  availableGameIds?: string[];
  loading?: boolean;
  /** The viewed window report's games (`header.window.gameIds`): a custom report's picks */
  windowGameIds?: string[];
}) {
  const { teamAbbrev } = useParams<{ teamAbbrev?: string }>();
  const environment = useLeagueEnvironment();
  const api = useLeagueApi();
  const { getReportsTeamPath } = useLeaguePaths();
  const teams = useReportStore(
    (store) => store.teamsByEnvironment[environment] ?? [],
  );
  const sortedTeams = useMemo(
    () =>
      [...teams].sort((a: Team, b: Team) =>
        `${a.city} ${a.name}`.localeCompare(`${b.city} ${b.name}`),
      ),
    [teams],
  );

  const seasonReports: SeasonChoice[] = [
    ...reportList.season.map((r) => ({ report: r, playoff: false, preseason: false })),
    ...reportList.playoff_season.map((r) => ({ report: r, playoff: true, preseason: false })),
    ...(reportList.preseason_season ?? []).map((r) => ({ report: r, playoff: false, preseason: true })),
  ].sort((a, b) => b.report.season - a.report.season);

  const selectedGame = reportList.game.find((r) => r.id === selectedReportId);
  const selectedSeason = seasonReports.find(
    ({ report }) => report.id === selectedReportId,
  );
  const isGameReport = !!selectedReportId?.includes("-game-");
  const viewedWindow = windowOf(selectedReportId);
  const viewedCustom = customOf(selectedReportId);
  // A window report (Last N or custom) sits under its regular season
  const windowSeasonNum = viewedWindow?.season ?? viewedCustom?.season;
  const windowSeason = windowSeasonNum
    ? seasonReports.find((sel) => !sel.playoff && !sel.preseason && sel.report.season === windowSeasonNum)
    : undefined;

  // What the picker shows when nothing is staged: the report being viewed.
  const viewedTeam = teamAbbrev?.toUpperCase() ?? "";
  const viewedSeasonId =
    (selectedSeason ??
      windowSeason ??
      (selectedGame && seasonReports.find((sel) => !sel.report.preview && gameInSeason(selectedGame, sel))))
      ?.report.id ??
    seasonReports[0]?.report.id ??
    "";
  const viewedScope: Scope = isGameReport
    ? "game"
    : viewedWindow
      ? viewedWindow.type
      : viewedCustom
        ? "custom"
        : "season";
  const viewedGameId = selectedGame?.id ?? "";
  const viewedPicks = viewedCustom ? (windowGameIds ?? []).join(",") : "";

  // What the picker shows: the viewed report, or a pick still waiting on its game (Single game) or its build (Custom).
  const [pendingTeam, setPendingTeam] = useState<string>(viewedTeam);
  const [pendingSeasonId, setPendingSeasonId] = useState<string>(viewedSeasonId);
  const [pendingScope, setPendingScope] = useState<Scope>(viewedScope);
  const [pendingGameId, setPendingGameId] = useState<string>(viewedGameId);
  const [pendingPicks, setPendingPicks] = useState<string[]>(viewedPicks ? viewedPicks.split(",") : []);
  const [customError, setCustomError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [switchingTeam, setSwitchingTeam] = useState(false);
  // A newly viewed report (or the list catching up with it: its game arrives with the schedule) resets the picker to it
  useEffect(() => {
    setPendingTeam(viewedTeam);
    setPendingSeasonId(viewedSeasonId);
    setPendingScope(viewedScope);
    setPendingGameId(viewedGameId);
    setPendingPicks(viewedPicks ? viewedPicks.split(",") : []);
    setCustomError(null);
  }, [viewedTeam, viewedSeasonId, viewedScope, viewedGameId, viewedPicks]);

  // The game dropdown only offers games from the season/type picked above —
  // no mixing playoffs into a regular-season selection.
  const pendingSeasonSel = seasonReports.find(
    ({ report }) => report.id === pendingSeasonId,
  );
  const gameReports = reportList.game.filter((g) =>
    pendingSeasonSel ? gameInSeason(g, pendingSeasonSel) : true,
  );
  // Windows are of a regular season, and offered only where built; custom picks are a regular season's games (NBA)
  const hasWindow = (sel: SeasonChoice | undefined, type: WindowType) =>
    !!sel && !sel.playoff && !sel.preseason && (reportList[type] ?? []).some((r) => r.season === sel.report.season);
  const allowsCustom = (sel: SeasonChoice | undefined) =>
    environment === ExplorerEnvironment.NBA && !!sel && !sel.playoff && !sel.preseason && !sel.report.preview;
  const windowScopes = WINDOW_TYPES.filter((t) => hasWindow(pendingSeasonSel, t));

  const currentTeam = sortedTeams.find(
    (team) => team.abbrev.toUpperCase() === teamAbbrev?.toUpperCase(),
  );

  // Custom: the regular season's games of the team being viewed
  const customSeason = allowsCustom(pendingSeasonSel) ? pendingSeasonSel!.report.season : null;
  const showCustomPicker = pendingScope === "custom" && customSeason !== null && pendingTeam === viewedTeam;
  const [customGames, setCustomGames] = useState<{
    key: string;
    games: PickerGame[];
    players: PickerPlayer[];
  } | null>(null);
  const customGamesKey = currentTeam && customSeason !== null ? `${currentTeam.abbrev}-${customSeason}` : null;
  useEffect(() => {
    if (!showCustomPicker || !currentTeam || customSeason === null || customGames?.key === customGamesKey) return;
    let cancelled = false;
    api
      .getCustomReportGames(currentTeam, customSeason)
      .then(({ games, players }) => {
        if (!cancelled) setCustomGames({ key: customGamesKey as string, games, players });
      })
      .catch((err) => console.error("Error fetching the custom games:", err));
    return () => {
      cancelled = true;
    };
  }, [api, showCustomPicker, currentTeam, customSeason, customGamesKey, customGames?.key]);

  const viewingLabel = (() => {
    const teamLabel = currentTeam
      ? `${currentTeam.city} ${currentTeam.name}`
      : (teamAbbrev ?? "");
    const reportLabel = selectedGame
      ? `${gameReportLabel(selectedGame)} · ${dayjs(selectedGame.gameInfo.date).format("YYYY")}`
      : selectedSeason
        ? seasonReportLabel(selectedSeason.report, selectedSeason.playoff, selectedSeason.preseason)
        : viewedWindow && windowSeason
          ? `${seasonLabel(windowSeason.report.season)} · ${WINDOW_LABELS[viewedWindow.type]}`
          : viewedCustom
            ? `${seasonLabel(viewedCustom.season)} · Custom${
                windowGameIds?.length ? ` · ${windowGameIds.length} ${windowGameIds.length === 1 ? "game" : "games"}` : ""
              }`
            : "";
    return [teamLabel, reportLabel].filter(Boolean).join(" · ").toUpperCase();
  })();

  const open = (id: string) => {
    if (id && id !== selectedReportId) onReportChange?.(id);
  };
  const viewedAbbrev = teamAbbrev?.toUpperCase() ?? "";

  const pickTeam = (abbrev: string) => {
    setPendingTeam(abbrev);
    const target = sortedTeams.find((t) => t.abbrev.toUpperCase() === abbrev);
    if (!target || abbrev === viewedAbbrev) return;
    const game = reportList.game.find((r) => r.id === pendingGameId);
    const staged = { seasonSel: pendingSeasonSel, scope: pendingScope, game };
    // Report ids are per team and the page keys its lists to the route's team, so this is a page load, not a re-render
    const go = (list: TeamReportList | null) => {
      const id = teamSwitchReportId(abbrev, staged, list);
      window.location.href = `${getReportsTeamPath(abbrev)}${id ? `?report=${encodeURIComponent(id)}` : ""}`;
    };
    setSwitchingTeam(true);
    api
      .getTeamReportList(target)
      .then(({ data }) => go(data))
      .catch(() => go(null));
  };

  const pickSeason = (seasonId: string) => {
    setPendingSeasonId(seasonId);
    setPendingGameId("");
    const sel = seasonReports.find(({ report }) => report.id === seasonId);
    if (pendingScope === "game" || (pendingScope === "custom" && allowsCustom(sel))) return;
    if (pendingScope !== "season" && pendingScope !== "custom" && hasWindow(sel, pendingScope)) {
      open(`${viewedAbbrev}-${pendingScope}-${sel!.report.season}`);
      return;
    }
    // The season has no such window (or no custom games): its full season
    setPendingScope("season");
    open(seasonId);
  };

  const pickScope = (scope: Scope) => {
    setPendingScope(scope);
    setCustomError(null);
    if (scope === "season") open(pendingSeasonId);
    else if (scope !== "game" && scope !== "custom" && pendingSeasonSel) {
      open(`${viewedAbbrev}-${scope}-${pendingSeasonSel.report.season}`);
    }
  };

  const pickGame = (gameId: string) => {
    setPendingGameId(gameId);
    open(gameId);
  };

  const requestCustom = () => {
    if (!currentTeam || customSeason === null || !pendingPicks.length) return;
    setRequesting(true);
    setCustomError(null);
    api
      .requestCustomReport(currentTeam, customSeason, pendingPicks)
      .then(({ key }) => open(customReportId(currentTeam.abbrev.toUpperCase(), key, customSeason)))
      .catch((err) => setCustomError(err?.response?.data?.error ?? "Could not request the custom report."))
      .finally(() => setRequesting(false));
  };

  return (
    <div className="trd trd-webonly">
      <div className="picker">
        <div className="brand">
          {/* The org's own black-pinwheel asset inverted to white — its
              stripe gaps are transparent, so the invert keeps the pinwheel
              shape (the CDN team logo renders as a solid blob when
              inverted, and the design project's white PNG exceeds the
              256KiB design-file read cap). */}
          <img
            src="/images/blazers-logo-black.png"
            alt="Portland Trail Blazers"
            style={{ filter: "invert(1)" }}
          />
          <span>
            Team
            <br />
            Reports
          </span>
        </div>
        {sortedTeams.length > 0 && (
          <div className="fld">
            <label htmlFor="pk-team">Team</label>
            <select
              id="pk-team"
              value={pendingTeam}
              disabled={loading || switchingTeam}
              onChange={(e) => pickTeam(e.target.value)}
            >
              {sortedTeams.map((team) => (
                <option key={team.abbrev} value={team.abbrev.toUpperCase()}>
                  {team.city} {team.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="fld">
          <label htmlFor="pk-season">Season</label>
          <select
            id="pk-season"
            value={pendingSeasonId}
            disabled={loading || switchingTeam}
            onChange={(e) => pickSeason(e.target.value)}
          >
            {seasonReports.map(({ report, playoff, preseason }) => (
              <option key={report.id} value={report.id}>
                {seasonReportLabel(report, playoff, preseason)}
              </option>
            ))}
          </select>
        </div>
        <div className="fld">
          <label htmlFor="pk-scope">Scope</label>
          <select
            id="pk-scope"
            value={pendingScope}
            disabled={loading || switchingTeam}
            onChange={(e) => pickScope(e.target.value as Scope)}
          >
            <option value="season">Full season</option>
            {windowScopes.map((t) => (
              <option key={t} value={t}>
                {WINDOW_LABELS[t]}
              </option>
            ))}
            <option value="game">Single game</option>
            {customSeason !== null && <option value="custom">Custom games</option>}
          </select>
        </div>
        {pendingScope === "game" && (
          <div className="fld">
            <label htmlFor="pk-game">Game</label>
            <select
              id="pk-game"
              value={pendingGameId}
              disabled={loading || switchingTeam}
              onChange={(e) => pickGame(e.target.value)}
            >
              <option value="">
                Choose game — {gameReports.length} played
              </option>
              {gameReports.map((report) => {
                const processing = !availableGameIds.includes(
                  report.gameInfo.gameId,
                );
                return (
                  <option
                    key={report.id}
                    value={report.id}
                    disabled={processing}
                  >
                    {gameReportLabel(report)}
                    {processing ? " (Processing)" : ""}
                  </option>
                );
              })}
            </select>
          </div>
        )}
        {showCustomPicker && (
          <button
            className="go"
            type="button"
            disabled={loading || requesting || !pendingPicks.length}
            onClick={requestCustom}
          >
            View Report
          </button>
        )}
      </div>
      {showCustomPicker && (
        <div className="custom-games">
          {customGames?.key === customGamesKey ? (
            <GamePicker
              games={customGames!.games}
              players={customGames!.players}
              picked={pendingPicks}
              onChange={setPendingPicks}
            />
          ) : (
            <div className="custom-games-note">Loading the season's games…</div>
          )}
          {customError && <div className="custom-games-note error">{customError}</div>}
        </div>
      )}
      <div className="viewing">
        <span className="vk">Viewing</span>
        <b>{viewingLabel}</b>
      </div>
    </div>
  );
}
